import { Card, CardContent } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { useTranslation } from 'react-i18next';
import { Building2, Smartphone, CreditCard, Wallet, Star, Trash2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import type { PayoutDestination } from '../types/payout.types';
import {
  payoutDestinationLabel,
  payoutDestinationAccountInfo,
  payoutDestinationIconKind,
  type PayoutDestinationIconKind,
} from '../helpers/payout-destination-display';
import { useSetDefaultPayoutMethod, useDeletePayoutMethod } from '../hooks/use-payout-methods';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/molecules/alert-dialog';

interface PayoutMethodCardProps {
  method: PayoutDestination;
  onSelect?: (method: PayoutDestination) => void;
  isSelected?: boolean;
  showActions?: boolean;
}

const ICON_BY_KIND: Record<PayoutDestinationIconKind, React.ElementType> = {
  bank: Building2,
  mobile: Smartphone,
  psp: CreditCard,
  generic: Wallet,
};

const ICON_STYLE_BY_KIND: Record<PayoutDestinationIconKind, string> = {
  bank: 'bg-blue-100 text-blue-600',
  mobile: 'bg-green-100 text-green-600',
  psp: 'bg-purple-100 text-purple-600',
  generic: 'bg-gray-100 text-gray-600',
};

export function PayoutMethodCard({ method, onSelect, isSelected, showActions = true }: PayoutMethodCardProps) {
  const { t } = useTranslation();
  const setDefault = useSetDefaultPayoutMethod();
  const deleteMethod = useDeletePayoutMethod();

  const iconKind = payoutDestinationIconKind(method);
  const Icon = ICON_BY_KIND[iconKind];
  const isApproved = method.approvalStatus === 'APPROVED';

  const getDisplayName = () => payoutDestinationLabel(method);
  const getAccountInfo = () => payoutDestinationAccountInfo(method);

  const handleSetDefault = async () => {
    try {
      await setDefault.mutateAsync(method.id);
    } catch (error) {
      console.error('Failed to set default:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMethod.mutateAsync(method.id);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected
          ? 'border-primary ring-2 ring-primary/20'
          : 'hover:border-primary/50'
      } ${!method.isVerified || !isApproved ? 'opacity-60' : ''}`}
      onClick={() => method.isVerified && isApproved && onSelect?.(method)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`rounded-full p-2 ${ICON_STYLE_BY_KIND[iconKind]}`}>
              <Icon className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{getDisplayName()}</span>
                {method.isDefault && (
                  <Badge variant="secondary" className="text-xs">
                    <Star className="size-3 mr-1" />
                    {t('payouts.methods.default')}
                  </Badge>
                )}
                {method.isVerified ? (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                    <CheckCircle2 className="size-3 mr-1" />
                    {t('payouts.methods.verified')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-200">
                    <XCircle className="size-3 mr-1" />
                    {t('payouts.methods.unverified')}
                  </Badge>
                )}
                {!isApproved && (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                    <Clock className="size-3 mr-1" />
                    {t(
                      method.approvalStatus === 'REJECTED'
                        ? 'payouts.destinations.approvalStatus.REJECTED'
                        : 'payouts.destinations.approvalStatus.PENDING_APPROVAL'
                    )}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{getAccountInfo()}</p>
            </div>
          </div>

          {showActions && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {!method.isDefault && method.isVerified && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSetDefault}
                  disabled={setDefault.isPending}
                >
                  <Star className="size-4" />
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={deleteMethod.isPending}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('payouts.methods.deleteTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('payouts.methods.deleteDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      {t('common.delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
